import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Platform Admin financials (GET /api/admin/financials). Every amount arrives
// from the server in cents, as Stripe reports it, and is formatted as dollars
// here and only here. Comped companies are already excluded by the server.
// The server caches for 10 minutes; Refresh asks it to recompute from Stripe.

const SHORT_LIST = 10;

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
export const formatCents = (cents) => usd.format((Number(cents) || 0) / 100);

const central = (iso, withTime) => (iso ? new Date(iso).toLocaleString('en-US', {
  timeZone: 'America/Chicago',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
}) : '-');

const TIER_NAMES = { basic: 'Basic', pro: 'Pro', premium: 'Premium', enterprise: 'Enterprise', unknown: 'Unknown price' };
const tierName = (t) => TIER_NAMES[t] || t;

function MoneyCard({ label, money }) {
  return (
    <div className="fin-card">
      <div className="fin-value">{formatCents(money.net)}</div>
      <div className="fin-label">{label}</div>
      {money.refunded > 0 && (
        <div className="fin-note">{formatCents(money.gross)} collected, {formatCents(money.refunded)} refunded</div>
      )}
    </div>
  );
}

function ShortList({ title, items, empty, render }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, SHORT_LIST);
  return (
    <div className="fin-list">
      <h4>{title} ({items.length})</h4>
      {items.length === 0 ? (
        <p className="fin-empty">{empty}</p>
      ) : (
        <ul>
          {shown.map(render)}
        </ul>
      )}
      {items.length > SHORT_LIST && (
        <button type="button" className="btn btn-sm btn-secondary" onClick={() => setAll(!all)}>
          {all ? 'Show fewer' : `Show all ${items.length}`}
        </button>
      )}
    </div>
  );
}

function AdminFinancials({ refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (refresh) => {
    if (refresh) setRefreshing(true);
    setError('');
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/financials${refresh ? '?refresh=1' : ''}`
      );
      setData(res.data);
    } catch (err) {
      setError(err.response?.status === 503
        ? 'Stripe is not configured on this server, so there are no financials to show.'
        : (err.response?.data?.error || 'Could not load financials. Please try again.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // refreshKey: AdminPanel bumps it on pull-to-refresh.
  useEffect(() => { load(false); }, [load, refreshKey]);

  // This season's paid invoices as CSV. Fetched with the session's
  // Authorization header (a plain link would carry none), then saved through
  // a blob URL, as the commercial waitlist export is.
  const [exporting, setExporting] = useState(false);
  const exportInvoices = async () => {
    setExporting(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/financials/invoices.csv`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `paid-invoices-${data ? data.revenue.season.label : 'season'}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export the invoices. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const totals = data ? data.tiers.reduce((acc, t) => ({
    snowPaying: acc.snowPaying + t.snowtrack.paying,
    snowMrr: acc.snowMrr + t.snowtrack.mrr,
    lawnPaying: acc.lawnPaying + t.lawntrack.paying,
    lawnMrr: acc.lawnMrr + t.lawntrack.mrr,
  }), { snowPaying: 0, snowMrr: 0, lawnPaying: 0, lawnMrr: 0 }) : null;

  return (
    <section className="fin-section" aria-labelledby="fin-heading">
      <div className="fin-header">
        <h2 id="fin-heading">Financials</h2>
        <div className="fin-actions">
          {data && <span className="fin-updated">Last updated {central(data.cachedAt, true)}</span>}
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => load(true)}
            disabled={loading || refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          {data && (
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={exportInvoices}
              disabled={exporting}
            >
              {exporting ? 'Exporting...' : 'Export season invoices CSV'}
            </button>
          )}
        </div>
      </div>

      {loading && <p className="fin-empty">Loading financials from Stripe...</p>}
      {error && <div className="alert alert-danger fin-error">{error}</div>}

      {data && (
        <>
          <div className="fin-cards">
            <div className="fin-card">
              <div className="fin-value">{formatCents(data.mrr.total)}</div>
              <div className="fin-label">MRR</div>
              <div className="fin-note">
                SnowTrack {formatCents(data.mrr.snowtrack)}, LawnTrack {formatCents(data.mrr.lawntrack)}
              </div>
            </div>
            <MoneyCard label="Revenue this month" money={data.revenue.thisMonth} />
            <MoneyCard label="Revenue last month" money={data.revenue.lastMonth} />
            <MoneyCard label={`Revenue this season (${data.revenue.season.label})`} money={data.revenue.season} />
            <MoneyCard label="Revenue all time" money={data.revenue.allTime} />
          </div>

          <div className="fin-table-wrap">
            <table className="fin-table">
              <thead>
                <tr>
                  <th rowSpan="2">Tier</th>
                  <th colSpan="2">SnowTrack</th>
                  <th colSpan="2">LawnTrack</th>
                </tr>
                <tr>
                  <th>Paying</th>
                  <th>MRR</th>
                  <th>Paying</th>
                  <th>MRR</th>
                </tr>
              </thead>
              <tbody>
                {data.tiers.length === 0 && (
                  <tr><td colSpan="5" className="fin-empty">No subscriptions yet.</td></tr>
                )}
                {data.tiers.map((t) => (
                  <tr key={t.tier}>
                    <td>{tierName(t.tier)}</td>
                    <td>{t.snowtrack.paying}</td>
                    <td>{formatCents(t.snowtrack.mrr)}</td>
                    <td>{t.lawntrack.paying}</td>
                    <td>{formatCents(t.lawntrack.mrr)}</td>
                  </tr>
                ))}
              </tbody>
              {data.tiers.length > 0 && (
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td>{totals.snowPaying}</td>
                    <td>{formatCents(totals.snowMrr)}</td>
                    <td>{totals.lawnPaying}</td>
                    <td>{formatCents(totals.lawnMrr)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <ul className="fin-facts">
            <li>Trials in progress: <strong>{data.trials.inProgress}</strong></li>
            <li>Cancellations, last 30 days: <strong>{data.cancellations.last30Days}</strong></li>
            <li>
              Renewals, next 30 days: <strong>{data.renewals.next30Days}</strong>
              {' '}({formatCents(data.renewals.amount)} expected)
            </li>
            <li>Comped companies left out: <strong>{data.excluded.compedCompanies}</strong></li>
          </ul>

          <div className="fin-lists">
            <ShortList
              title="Failed payments"
              items={data.failedPayments}
              empty="None."
              render={(f) => (
                <li key={f.invoiceId}>
                  <span className="fin-who">{f.company}</span>
                  <span>{formatCents(f.amountDue)} {f.status === 'uncollectible' ? 'uncollectible' : 'past due'}</span>
                </li>
              )}
            />
            <ShortList
              title="Trials ending within 7 days"
              items={data.trials.endingSoon}
              empty="None."
              render={(t) => (
                <li key={`${t.companyId}-${t.trialEnd}`}>
                  <span className="fin-who">{t.company}</span>
                  <span>{tierName(t.tier)}, ends {central(t.trialEnd)}</span>
                </li>
              )}
            />
            <ShortList
              title="Unmatched Stripe customers"
              items={data.unmatched}
              empty="None. Every Stripe customer belongs to a company."
              render={(u) => (
                <li key={u.customerId}>
                  <span className="fin-who fin-mono">{u.customerId}</span>
                  <span>
                    {formatCents(u.paidAllTime)} paid
                    {u.liveSubscriptions > 0 ? `, ${u.liveSubscriptions} live subscription${u.liveSubscriptions === 1 ? '' : 's'}` : ''}
                  </span>
                </li>
              )}
            />
          </div>
          {data.unmatched.length > 0 && (
            <p className="fin-note">
              Unmatched customers have no company with that Stripe customer id. Their money is not in the figures above.
            </p>
          )}
        </>
      )}
      <style>{styles}</style>
    </section>
  );
}

const styles = `
  .fin-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  .fin-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px 16px;
    margin-bottom: 16px;
  }
  .fin-header h2 { margin: 0; font-size: 1.25rem; }
  .fin-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; }
  .fin-updated { font-size: 0.85rem; color: var(--color-text-light, #6b7280); }
  .fin-error { margin: 0 0 16px; }
  .fin-cards {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 20px;
  }
  .fin-card {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 14px;
    min-width: 0;
  }
  .fin-value { font-size: 1.5rem; font-weight: 700; color: #1e293b; overflow-wrap: anywhere; }
  .fin-label { font-size: 0.85rem; color: #475569; margin-top: 4px; }
  .fin-note { font-size: 0.78rem; color: var(--color-text-light, #6b7280); margin-top: 6px; }
  .fin-table-wrap { overflow-x: auto; margin-bottom: 16px; }
  .fin-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
  .fin-table th, .fin-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #e5e7eb;
    text-align: right;
    white-space: nowrap;
  }
  .fin-table th:first-child, .fin-table td:first-child { text-align: left; }
  .fin-table thead th { background: #f8fafc; color: #475569; font-weight: 600; text-align: center; }
  .fin-table tfoot td { font-weight: 700; }
  .fin-facts {
    list-style: none;
    padding: 0;
    margin: 0 0 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px 24px;
    font-size: 0.9rem;
    color: #334155;
  }
  .fin-lists { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
  .fin-list h4 { margin: 0 0 8px; font-size: 0.95rem; }
  .fin-list ul { list-style: none; padding: 0; margin: 0 0 8px; }
  .fin-list li {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 2px 12px;
    padding: 6px 0;
    border-bottom: 1px solid #f1f5f9;
    font-size: 0.88rem;
  }
  .fin-who { font-weight: 600; overflow-wrap: anywhere; }
  .fin-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-weight: 500; font-size: 0.82rem; }
  .fin-empty { color: var(--color-text-light, #6b7280); font-size: 0.88rem; margin: 0; }
  @media (max-width: 1024px) {
    .fin-cards { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .fin-lists { grid-template-columns: 1fr; }
  }
  @media (max-width: 640px) {
    .fin-section { padding: 16px; }
    .fin-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .fin-value { font-size: 1.2rem; }
  }
`;

export default AdminFinancials;
